import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../components/General/BreadCrumbs";
import { Chevron, Success, Warning } from "../../components/Icons";
import LoadingSkeleton from "../../components/UI/LoadingSkeleton";
import Button from "../../components/UI/Button";
import { selectUser } from "../../store/userSlice";
import PersonalInfo from "./PersonalInfo";
import PreviewVehicle from "./PreviewVehicle";
import { defaultValues, fetchVehicleData, stateType, submitToSellVehicle } from "./utils";
import VehicleInfo from "./VehicleInfo";
import Modal from "../../components/UI/Modal";
import modalStyle from "../../components/Vehicles/RequestVehicle/index.module.css";
import sidebarStyle from "../VehiclePage/index.module.css";
import style from "./index.module.css";
import { VehicleNames, VehicleTypesT } from "../../utils/GeneralTypes";
import { rawPhoneNumber, useScrollToTop } from "../../utils/generalUtils";
import VehicleAttributes from "./VehicleAttributes";
import { VehicleCategoriesStep } from "./VehicleCategoriesStep";
import { useTranslation } from "../../hooks/useTranslation";
import { useHistory, useParams } from "react-router-dom";

const SellVehicle = ({ isEditing = false, vehicleNames }: ComponentPropsType) => {
  const { vehicleID } = useParams<{
    vehicleID: string;
  }>();
  const { user } = useSelector(selectUser);
  const { locale } = useTranslation();
  const history = useHistory();

  const initialMount = useRef<boolean>(true);

  useScrollToTop();

  const vehicleType: VehicleTypesT = vehicleNames?.single;

  const [componentData, setComponentData] = useState<stateType>({
    isFetching: true,
    data: {
      brands: [],
      brandsDependants: [],
      models: [],
      modelsDependants: [],
      modelYears: [],
      cities: [],
      citiesDependants: [],
      regions: [],
      attributes: [],
      categories_0: [],
      categories_0Dependants: [],
      // categories_1: [],
      // categories_1Dependants: [],
      categories: [],
      editingVehicle: null,
    },
    activePage: 0,
  });

  const [submitState, setSubmitState] = useState<{
    isSubmitting: boolean;
    error: string;
    success: boolean;
    newVehicleId?: number;
  }>({
    isSubmitting: false,
    error: "",
    success: false,
    newVehicleId: undefined,
  });

  const [images, setImages] = useState<{
    deleted: number[];
  }>({ deleted: [] });

  const { handleSubmit, watch, register, setValue, reset, setError, formState, trigger, control } = useForm({
    defaultValues: {
      ...defaultValues,
      ...(!isEditing ? { acknowledgement: true } : {}),
      ...(user
        ? {
            name: user.name,
            phone: +rawPhoneNumber(user.phone),
            email: user.email,
          }
        : {}),
    },
  });

  const watchedInputs = watch();

  useEffect(() => {
    if (!user) {
      history.replace("/sign");
    } else {
      if (!initialMount.current) {
        setComponentData((current) => ({
          ...current,
          isFetching: true,
        }));
      }
      initialMount.current = false;
      (async () => {
        //// default is car fetchers if not editing
        //// on every change vehicle data will be populated
        const data = await fetchVehicleData(vehicleType, isEditing, vehicleID, "used");
        const attributes = data.attributes.reduce(
          (acc: any, cur: any) => ({
            ...acc,
            [cur.id]: cur.shape === "checkbox" ? false : cur.shape === "text" ? "" : "no_option",
          }),
          {},
        );
        reset({
          ...defaultValues,
          ...(!isEditing ? { acknowledgement: true } : {}),
          ...(user
            ? {
                name: user.name,
                phone: +rawPhoneNumber(user.phone),
                email: user.email,
              }
            : {}),
          attributes: attributes,
        });
        if (isEditing && data.editingVehicle) {
          // TODO: enable for category level 2
          // if (data.editingVehicle?.vehicle_category_parent_id) {
          //   setValue("category_0", data.editingVehicle.vehicle_category_parent_id.toString());
          // }
          // if (data.editingVehicle?.vehicle_category_id) {
          //   setValue("category_1", data.editingVehicle.vehicle_category_id.toString());
          // }
          if (data.editingVehicle?.vehicle_category_id) {
            setValue("category_0", data.editingVehicle.vehicle_category_id.toString());
          }

          if (data.editingVehicle?.model?.brand?.id) setValue("brand", data.editingVehicle.model.brand.id.toString());
          if (data.editingVehicle?.model?.id) setValue("brand_model", data.editingVehicle.model.id.toString());
          if (data.editingVehicle?.model?.year) setValue("model_id", data.editingVehicle.model.year);
          if (data.editingVehicle?.milage) setValue("milage", data.editingVehicle.milage);
          if (data.editingVehicle?.price) setValue("price", data.editingVehicle.price);
          if (data.editingVehicle?.rentable) setValue("rentable", data.editingVehicle.rentable);
          if (data.editingVehicle?.rent) setValue("rent", data.editingVehicle.rent);
          if (data.editingVehicle?.seller?.region?.city?.id)
            setValue("city", data.editingVehicle.seller.region.city.id.toString());
          if (data.editingVehicle?.seller?.region?.id)
            setValue("region_id", data.editingVehicle.seller.region.id.toString());
          if (data.editingVehicle?.description) setValue("description", data.editingVehicle.description);
          if (data.editingVehicle?.license_id) setValue("license_id", data.editingVehicle.license_id);
          if (data.editingVehicle?.images)
            setValue(
              "images",
              (data.editingVehicle.images as { id: number; image: string }[]).map(({ id, image }) => {
                return {
                  id,
                  file: image,
                };
              }),
            );
          if (data.editingVehicle?.attributes) {
            setValue("attributes", {
              ...attributes,
              ...data.editingVehicle.attributes?.equipments?.reduce(
                (acc: any, cur: any) => ({
                  ...acc,
                  [cur.id]: true,
                }),
                {},
              ),
              ...data.editingVehicle.attributes?.highlights?.reduce(
                (acc: any, cur: any) => ({
                  ...acc,
                  [cur.id]: cur?.saved_value,
                }),
                {},
              ),
            });
          }
        }
        setComponentData({
          isFetching: false,
          data,
          activePage: 0,
        });
      })();
    }
  }, [vehicleType, reset, user, history, isEditing, vehicleID, setValue]);

  // we need this because in case of trucks we need to filter the attributes list first based on selected category
  const attributesWillBeShowenLength = watchedInputs.vehicle_category_1
    ? componentData.data.attributes.filter(
        (a) => a.parentTruck?.toString() === watchedInputs.vehicle_category_1.toString(),
      ).length
    : componentData.data.attributes.length;

  const steps = [
    ...(!isEditing
      ? [
          {
            title: locale("about_your_vehicle"),
            nextStepLebal: locale("next"),
            Component: (
              <VehicleCategoriesStep
                vehicleName={vehicleNames}
                nextPageHandler={() => onChangePageHandler("next")}
                register={register}
                error={formState?.errors?.category_1?.message as string | undefined}
                vehicleType={vehicleType}
                isEditing={isEditing}
                setValue={setValue}
              />
            ),
          },
        ]
      : []),
    {
      title: locale("about_your_vehicle"),
      nextStepLebal: locale("next"),
      Component: (
        <VehicleInfo
          key="step01vehicleinfo"
          componentData={componentData.data}
          watchedInputs={watchedInputs}
          vehicleType={vehicleType}
          register={register}
          control={control}
          setValue={setValue}
          defaultImages={componentData.data.editingVehicle?.images || []}
          deletedImages={images.deleted}
          setImages={setImages}
          formState={formState}
          isEditing={isEditing}
          setError={setError}
        />
      ),
    },
    ...(attributesWillBeShowenLength
      ? [
          {
            title: locale("about_your_vehicle"),
            nextStepLebal: locale("next"),
            Component: (
              <VehicleAttributes
                key="step02vehicleattr"
                componentData={componentData.data}
                watchedInputs={watchedInputs}
                vehicleType={vehicleType}
                register={register}
                setValue={setValue}
                formState={formState}
              />
            ),
          },
        ]
      : []),
    {
      title: locale("personal_info"),
      // title: null,
      nextStepLebal: null,
      needsAck: true,
      Component: <PersonalInfo key="step03personalinfo" register={register} isEditing={isEditing} />,
    },
    // {
    //   title: null,
    //   nextStepLebal: null,
    //   Component: (
    //     <PreviewVehicle
    //       key="step04previewVehicle"
    //       watchedInputs={watchedInputs}
    //       componentData={componentData.data}
    //       isEditing={isEditing}
    //       defaultImages={
    //         componentData.data.editingVehicle?.images
    //           ?.filter((img: any) => !images.deleted.includes(img.id.toString()))
    //           ?.map((img: any) => img.image) || []
    //       }
    //     />
    //   ),
    // },
  ];
  const onChangePageHandler = useCallback(
    async (type: "next" | "previous") => {
      // if the operation is previous no need to validate the current step inputs
      if (type === "previous" && componentData.activePage > 0) {
        setComponentData((current) => ({
          ...current,
          activePage: current.activePage - 1,
        }));
        return;
      }
      if (componentData.activePage !== steps.length - 1) await trigger();
      let err: string[] = [];
      err = Object.keys(formState.errors);

      if (!err.length) {
        window.scrollTo(0, 0);
        setComponentData((current) => ({
          ...current,
          activePage: type === "next" ? current.activePage + 1 : current.activePage - 1,
        }));
      }
    },
    [componentData.activePage, steps.length, trigger, formState.errors],
  );

  const onSubmitHandler = useCallback(
    // TODO: fix this type
    async (data: any) => {
      setSubmitState({
        isSubmitting: true,
        error: "",
        success: false,
      });
      let submitError = "",
        submitSuccess = false,
        newVehicleId: undefined;

      try {
        const res = await submitToSellVehicle(
          vehicleType,
          data,
          isEditing,
          vehicleID,
          images.deleted,
          componentData.data.editingVehicle?.attributes,
          componentData.data.attributes,
        );
        submitSuccess = true;
        newVehicleId = res.data.vehicle.id;
      } catch (error) {
        submitError = error instanceof Error ? error.message : locale("error_occured");
      }
      setSubmitState({
        isSubmitting: false,
        error: submitError,
        success: submitSuccess,
        newVehicleId,
      });
    },
    [
      vehicleType,
      isEditing,
      vehicleID,
      images,
      componentData.data.editingVehicle,
      componentData.data.attributes,
      locale,
    ],
  );

  const onCloseResponseModal = useCallback(
    (success: boolean, newVehicleId?: number) => {
      if (success && newVehicleId) {
        history.push(`/used-${vehicleNames.plural}/${vehicleNames.single}/${newVehicleId}`);
      } else setSubmitState({ isSubmitting: false, error: "", success: false });
    },
    [history, vehicleNames],
  );

  const activeStep = steps[componentData.activePage];

  return (
    <>
      {(submitState.success || submitState.error) && (
        <Modal onClose={() => onCloseResponseModal(submitState.success, submitState.newVehicleId)}>
          <div className={modalStyle.modal}>
            <div className={modalStyle.modal_response}>
              {submitState.success ? (
                <Success width="50" height="50" color="#35b545" />
              ) : (
                <Warning width="50" height="50" color="#dc0000" />
              )}
              <p>{submitState.success ? locale("ad_placed_successfuly") : submitState.error}</p>
            </div>
          </div>
        </Modal>
      )}
      {/* <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <BreadCrumbs
              pages={[
                { text: locale("home"), link: "/" },
                ...(isEditing
                  ? [
                      {
                        text: locale("my_vehicles"),
                        link: `/my-vehicles/${vehicleNames?.plural}`,
                      },
                      {
                        text: locale("edit"),
                        link: "",
                      },
                    ]
                  : [
                      {
                        text: locale("sell_vehicle"),
                        link: "",
                      },
                    ]),
              ]}
            />
          </div>
        </div>
      </div> */}
      <div className="main_section">
        <div className="container-lg">
          <div className="section_content_padding">
            <form onSubmit={handleSubmit(onSubmitHandler)}>
              <div className={`${sidebarStyle.page_row} row`}>
                {/* <div className="col-lg-4 sticky_sidebar">
                  {componentData.activePage === 3 && (
                    <SidebarBox>
                      <div className={sidebarStyle.sidebar_box}>
                        <p className={sidebarStyle.text}>
                          {locale("your_ad_ready")}
                        </p>
                        <Button
                          styleType="primary"
                          buttonType="submit"
                          isLoading={submitState.isSubmitting}
                          disabled={submitState.isSubmitting}
                        >
                          {locale("publish")}
                        </Button>
                      </div>
                    </SidebarBox>
                  )}
                </div> */}
                {/* <div className="col-lg-8"> */}
                <div className="col-12">
                  {((componentData.data.brands && !componentData.isFetching) || componentData.isFetching) &&
                    (componentData.isFetching ? (
                      <LoadingSkeleton count={20} />
                    ) : (
                      <>
                        {componentData.activePage !== steps.length - 1 && (
                          <h3 className="main_section__title main_section__title--center">{activeStep.title}</h3>
                        )}
                        {activeStep.Component}
                        <div className={style.page_footer}>
                          {componentData.activePage !== 0 && (
                            <button
                              onClick={() => onChangePageHandler("previous")}
                              className={style.previous}
                              type="button"
                            >
                              <span>
                                <Chevron height="12" width="12" color="#fff" />
                              </span>
                              {locale("previous")}
                            </button>
                          )}
                          {componentData.activePage !== steps.length - 1 ? (
                            <button
                              onClick={() => onChangePageHandler("next")}
                              className={style.next}
                              type="button"
                              disabled={!isEditing && activeStep.needsAck === true && !watchedInputs.acknowledgement}
                            >
                              {activeStep.nextStepLebal}
                              <span>
                                <Chevron height="12" width="12" color="#fff" />
                              </span>
                            </button>
                          ) : null}
                          {componentData.activePage == steps.length - 1 ? (
                            <div className={style.publish_box}>
                              <p className={`${style.text} ${style.next}`}>{locale("your_ad_ready")}</p>
                              <Button
                                styleType="publish"
                                buttonType="submit"
                                isLoading={submitState.isSubmitting}
                                disabled={
                                  submitState.isSubmitting ||
                                  (!isEditing && activeStep.needsAck === true && !watchedInputs.acknowledgement)
                                }
                              >
                                {locale("publish")}
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </>
                    ))}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellVehicle;

type ComponentPropsType = {
  /**
   * is the operation insert or update
   */
  isEditing?: boolean;
  vehicleNames: VehicleNames;
};
